import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import type { OutcomeRow } from "./OutcomesList";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OutcomeCategory {
  id: number;
  name: string;
  value_type: string | null;
}

interface OutcomeFormValues {
  categoryId: string;
  outcomeType: string;
  outcomeValue: string;
  outcomeDate: string;
}

interface OutcomeFormProps {
  personId: string;
  outcome?: OutcomeRow | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OutcomeForm({ personId, outcome, onClose }: OutcomeFormProps) {
  const queryClient = useQueryClient();
  const isEditing = !!outcome;

  const { data: categories } = useQuery<OutcomeCategory[]>({
    queryKey: ["outcome-categories"],
    queryFn: async () => {
      const res = await api.get<{ data: OutcomeCategory[] }>(
        "/admin/outcome-categories",
      );
      return res.data;
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OutcomeFormValues>({
    defaultValues: {
      categoryId: outcome?.category_id ? String(outcome.category_id) : "",
      outcomeType: outcome?.outcome_type ?? "",
      outcomeValue: outcome?.outcome_value ?? "",
      outcomeDate:
        outcome?.outcome_date ?? new Date().toISOString().split("T")[0],
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: OutcomeFormValues) =>
      api.post(`/clients/${personId}/outcomes`, {
        categoryId: data.categoryId ? parseInt(data.categoryId, 10) : undefined,
        outcomeType: data.outcomeType || undefined,
        outcomeValue: data.outcomeValue || undefined,
        outcomeDate: data.outcomeDate || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outcomes", personId] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: OutcomeFormValues) =>
      api.put(`/clients/${personId}/outcomes/${outcome!.id}`, {
        categoryId: data.categoryId
          ? parseInt(data.categoryId, 10)
          : undefined,
        outcomeType: data.outcomeType || undefined,
        outcomeValue: data.outcomeValue || undefined,
        outcomeDate: data.outcomeDate || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outcomes", personId] });
      onClose();
    },
  });

  const mutation = isEditing ? updateMutation : createMutation;

  function onSubmit(data: OutcomeFormValues) {
    mutation.mutate(data);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">
          {isEditing ? "Edit Outcome" : "New Outcome"}
        </h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Outcome Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium leading-none text-foreground">
                  Category
                </label>
                <select
                  {...register("categoryId")}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select category...</option>
                  {(categories ?? []).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium leading-none text-foreground">
                  Type
                </label>
                <select
                  {...register("outcomeType")}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select type...</option>
                  <option value="pre">Pre (Intake)</option>
                  <option value="post">Post (Exit)</option>
                  <option value="milestone">Milestone</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <Input
              label="Date"
              type="date"
              required
              {...register("outcomeDate", {
                required: "Date is required",
              })}
              error={errors.outcomeDate?.message}
            />

            <div className="space-y-1.5">
              <label className="text-sm font-medium leading-none text-foreground">
                Value / Result
              </label>
              <textarea
                {...register("outcomeValue")}
                rows={4}
                placeholder="Describe the outcome measurement or result..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {/* Error */}
            {mutation.isError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {(mutation.error as Error)?.message ?? "An error occurred"}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="submit" loading={mutation.isPending}>
                {isEditing ? "Save Changes" : "Create Outcome"}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
